import { useCallback, useEffect, useRef } from 'react';
import { AddressElement } from '@stripe/react-stripe-js';
import type {
  StripeAddressElement as StripeAddressElementType,
  StripeAddressElementChangeEvent,
} from '@stripe/stripe-js';
import type {
  PaymentCard,
  PlanPeriod,
} from '@frameio/graphql-client/generated';
import {
  countriesThatForbidYearlyPlan,
  isYearlyPlanForbiddenInCountry,
  isYearlyPlanForbiddenInUsaState,
  usaStatesThatForbidYearlyPlan,
} from '../../plans/utils/isYearlyPlanForbidden';
import TEXT from './generated/TEXT/BillingStripeAddressElement';
import styles from './StripeAddressElement.module.css';

TEXT.DEFINE({
  /** Error message when yearly plan is forbidden in selected location. Use {selectedLocation} for the location name */
  yearlyPlanForbidden: (selectedLocation: string): string =>
    `The annual plan you're subscribed to is not available in ${selectedLocation}. To change your billing address to ${selectedLocation}, please switch to a monthly plan. For assistance, contact customer support.`,
});

type StripeAddressElementProps = {
  isEditing?: boolean;
  onElementCompleteChange: (isComplete: boolean) => void;
  onError: (message?: string) => void;
  paymentCard: PaymentCard;
  period: PlanPeriod;
  setUsersCountry?: (country: string) => void;
};

export default function StripeAddressElement({
  isEditing,
  onElementCompleteChange,
  onError,
  paymentCard,
  period,
  setUsersCountry,
}: StripeAddressElementProps) {
  const isCompleteRef = useRef(false);

  const currentlyBilledState = paymentCard.billingAddress?.state;
  const currentlyBilledCountry = paymentCard.billingAddress?.country;

  const handleChange = useCallback(
    (event: StripeAddressElementChangeEvent) => {
      if (event.complete !== isCompleteRef.current) {
        isCompleteRef.current = event.complete;
        onElementCompleteChange(event.complete);
      }

      const usersCountry = event.value?.address?.country ?? '';

      if (setUsersCountry) {
        setUsersCountry(usersCountry);
      }

      if (period === 'YEARLY') {
        const country = event.value?.address?.country ?? '';

        const state = event.value?.address?.state ?? '';

        const updatingToSameState = currentlyBilledState === state;
        const updatingToSameCountry = currentlyBilledCountry === country;

        const unableToUpdateCountry =
          isYearlyPlanForbiddenInCountry(country) && !updatingToSameCountry;
        const unableToUpdateState =
          isYearlyPlanForbiddenInUsaState(state) && !updatingToSameState;

        if (unableToUpdateCountry) {
          const message = TEXT.yearlyPlanForbidden(
            countriesThatForbidYearlyPlan[country]
          );
          onError(message);
        } else if (unableToUpdateState) {
          const message = TEXT.yearlyPlanForbidden(
            usaStatesThatForbidYearlyPlan[state]
          );
          onError(message);
        } else {
          onError(undefined);
        }
      }
    },
    [
      currentlyBilledCountry,
      currentlyBilledState,
      onElementCompleteChange,
      onError,
      period,
      setUsersCountry,
    ]
  );

  const addressElement = useRef<StripeAddressElementType | null>(null);

  useEffect(() => {
    if (isEditing) addressElement.current?.focus();
  }, [isEditing]);

  return (
    <div
      className={styles.container}
      onFocus={() => {
        addressElement.current?.focus();
      }}
      // tabIndex={0} delegates focus into the embedded Stripe AddressElement iframe via onFocus.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <AddressElement
        onChange={handleChange}
        onReady={(element: StripeAddressElementType) => {
          addressElement.current = element;
        }}
        options={{
          defaultValues: {
            name: paymentCard.card?.name,
            address: {
              line1: paymentCard.billingAddress?.line1,
              line2: paymentCard.billingAddress?.line2,
              city: paymentCard.billingAddress?.city,
              state: paymentCard.billingAddress?.state,
              postal_code: paymentCard.billingAddress?.postalCode,
              country: paymentCard.billingAddress?.country ?? '',
            },
          },
          mode: 'billing',
        }}
      />
    </div>
  );
}
